import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { firebaseConfig, VAPID_KEY } from '@/lib/firebaseConfig';
import { supabase } from '@/lib/supabaseClient';

function firebaseApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export async function pushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator) || typeof Notification === 'undefined') return false;
  try { return await isSupported(); } catch { return false; }
}

/**
 * Request permission, register the FCM service worker, mint a token, and persist it
 * to the subcontractor's row. Returns the token. Throws with a user-friendly message.
 */
export async function enablePushForSubcontractor(subcontractorId) {
  if (!subcontractorId) throw new Error('Missing subcontractor profile.');
  if (!(await pushSupported())) throw new Error('Push notifications are not supported in this browser.');
  if (!VAPID_KEY) throw new Error('Push is not configured yet (missing VAPID key).');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted.');

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  // Make sure the worker is active before requesting a token.
  await navigator.serviceWorker.ready;

  const messaging = getMessaging(firebaseApp());
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error('Could not obtain a notification token.');

  const { error } = await supabase
    .from('subcontractors')
    .update({ fcm_token: token })
    .eq('id', subcontractorId);
  if (error) throw error;

  return token;
}

// One refresh per app load per subcontractor — re-renders and route changes
// must not spam FCM/the DB with identical token writes.
const refreshedFor = new Set();

/**
 * Silent token refresh for every portal open. FCM tokens rot (browser reinstall,
 * SW update, token rotation) but the DB only learned them on the first grant —
 * so pushes were sent to dead tokens. When permission is ALREADY granted this
 * re-mints the current token (no prompt) and writes it to subcontractors.fcm_token.
 * Never throws; returns the token or null.
 */
export async function refreshPushTokenIfGranted(subcontractorId) {
  if (!subcontractorId || refreshedFor.has(subcontractorId)) return null;
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
    if (!(await pushSupported()) || !VAPID_KEY) return null;
    refreshedFor.add(subcontractorId);

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;

    const messaging = getMessaging(firebaseApp());
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return null;

    const { error } = await supabase
      .from('subcontractors')
      .update({ fcm_token: token })
      .eq('id', subcontractorId);
    if (error) throw error;

    return token;
  } catch (e) {
    console.warn('Push token refresh skipped:', e?.message || e);
    // A silent catch here is how push could stop working on every device at once
    // and leave no trace anywhere. Record the reason. `refreshedFor` was already
    // marked above, so this is at most one row per person per app load.
    recordRegistrationFailure(e);
    return null;
  }
}

/**
 * Tell the server that minting failed. Best-effort and deliberately unawaited by
 * the caller: observability must never be the thing that breaks the portal.
 */
function recordRegistrationFailure(e) {
  const reason = String(e?.code || e?.message || e || 'unknown').slice(0, 500);
  try {
    supabase
      .rpc('push_registration_failed', {
        p_reason: reason,
        p_user_agent: typeof navigator === 'undefined' ? null : navigator.userAgent,
      })
      .then(() => {}, () => {});
  } catch {
    /* nothing left to report to */
  }
}

// One refresh per app load for the admin, same reason as the crew side.
let adminRefreshed = false;

/**
 * Silent token refresh for an admin opening the admin portal. Same contract as
 * refreshPushTokenIfGranted: never prompts, never throws, returns the token or
 * null. The identity comes from the caller's own session inside the RPC, so
 * this cannot register a token against another admin.
 */
export async function refreshPushTokenForAdmin() {
  if (adminRefreshed) return null;
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
    if (!(await pushSupported()) || !VAPID_KEY) return null;
    adminRefreshed = true;

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;

    const messaging = getMessaging(firebaseApp());
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return null;

    const { error } = await supabase.rpc('admin_push_token_set', { p_token: token });
    if (error) throw error;
    return token;
  } catch (e) {
    console.warn('Admin push token refresh skipped:', e?.message || e);
    recordRegistrationFailure(e);
    return null;
  }
}

/**
 * Ask an admin for notification permission and register the device. Unlike the
 * refresher this DOES prompt, so it belongs behind a button the admin pressed.
 */
export async function enablePushForAdmin() {
  if (!(await pushSupported())) throw new Error('Push notifications are not supported in this browser.');
  if (!VAPID_KEY) throw new Error('Push is not configured yet (missing VAPID key).');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted.');

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  await navigator.serviceWorker.ready;
  const messaging = getMessaging(firebaseApp());
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  if (!token) throw new Error('Could not obtain a notification token.');

  const { error } = await supabase.rpc('admin_push_token_set', { p_token: token });
  if (error) throw error;
  adminRefreshed = true;
  return token;
}

// ── Partner portal users (20261313) ─────────────────────────────────────────
// Same contract as the admin pair: the RPC takes the identity from the
// caller's own session, so a token can only be registered to oneself.
let partnerRefreshed = false;

async function mintToken() {
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  await navigator.serviceWorker.ready;
  const messaging = getMessaging(firebaseApp());
  return getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
}

/** Silent refresh when a partner opens the portal: never prompts, never throws. */
export async function refreshPushTokenForPartner() {
  if (partnerRefreshed) return null;
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
    if (!(await pushSupported()) || !VAPID_KEY) return null;
    partnerRefreshed = true;
    const token = await mintToken();
    if (!token) return null;
    const { error } = await supabase.rpc('partner_push_token_set', { p_token: token });
    if (error) throw error;
    return token;
  } catch (e) {
    console.warn('Partner push token refresh skipped:', e?.message || e);
    recordRegistrationFailure(e);
    return null;
  }
}

/** Ask a partner for permission and register the device (behind a button). */
export async function enablePushForPartner() {
  if (!(await pushSupported())) throw new Error('This browser cannot show notifications. On an iPhone, add the portal to your home screen first.');
  if (!VAPID_KEY) throw new Error('Notifications are not set up yet.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications were not allowed. You can allow them in your browser or phone settings.');
  const token = await mintToken();
  if (!token) throw new Error('Could not register this device.');
  const { error } = await supabase.rpc('partner_push_token_set', { p_token: token });
  if (error) throw error;
  partnerRefreshed = true;
  return token;
}

/** Subscribe to foreground messages (app open). Returns an unsubscribe fn. */
export async function listenForegroundMessages(handler) {
  if (!(await pushSupported())) return () => {};
  const messaging = getMessaging(firebaseApp());
  return onMessage(messaging, handler);
}
